import React, { useCallback, useEffect, useState } from 'react';
import { Button, Loader } from '@vibe/core';
import {
  getBoardConfig,
  updateBoardConfig,
  getBoardColumns,
  getSubitemsBoardColumns,
  type FieldMapping,
  type MondayColumn,
  type ItemLevel,
} from '../../api/board-config.api';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Props ────────────────────────────────────────────────────────────────────

interface FieldMappingWizardProps {
  boardId: number;
  /** Called when the user finishes the wizard (mapping saved) */
  onComplete: () => void;
  /** Called when the user clicks "Skip" */
  onSkip: () => void;
}

// ─── Field definitions ────────────────────────────────────────────────────────

interface FieldDef {
  key: keyof FieldMapping;
  label: string;
  required: boolean;
  expectedType: string;
  /** When true, any column type is accepted (no type validation on save) */
  skipTypeValidation?: boolean;
}

const FIELD_DEFINITIONS: FieldDef[] = [
  {
    key: 'timelineColumnId',
    label: 'Timeline',
    required: true,
    expectedType: 'Timeline',
  },
  {
    key: 'assigneesColumnId',
    label: 'Assignee',
    required: true,
    expectedType: 'People',
  },
  {
    key: 'scheduledHoursColumnId',
    label: 'Original Estimate',
    required: false,
    expectedType: 'Numbers',
  },
  {
    key: 'scheduledHoursPerDayColumnId',
    label: 'Hours Per Day',
    required: false,
    expectedType: 'Numbers',
  },
  {
    key: 'statusColumnId',
    label: 'Status',
    required: false,
    expectedType: 'Status',
    skipTypeValidation: true,
  },
  {
    key: 'colorColumnId',
    label: 'Color',
    required: false,
    expectedType: 'Color Picker',
  },
];

const EMPTY_MAPPING: FieldMapping = {
  timelineColumnId: null,
  assigneesColumnId: null,
  scheduledHoursColumnId: null,
  scheduledHoursPerDayColumnId: null,
  statusColumnId: null,
  colorColumnId: null,
};

type WizardStep = 'item-level' | 'mapping';

// ─── Mapping Table Sub-component ─────────────────────────────────────────────

interface MappingTableProps {
  label: string;
  boardName: string;
  mapping: FieldMapping;
  columns: MondayColumn[];
  onFieldChange: (key: keyof FieldMapping, columnId: string | null) => void;
}

const MappingTable: React.FC<MappingTableProps> = ({
  label,
  boardName,
  mapping,
  columns,
  onFieldChange,
}) => (
  <>
    <div className="rp-mapping-wizard__subtitle">
      <span>{label}</span>
      <span className="rp-mapping-wizard__required-note">
        *Required field to show item in Scheduler
      </span>
    </div>

    <div className="rp-mapping-wizard__table">
      <div className="rp-mapping-wizard__table-header">
        <div className="rp-mapping-wizard__table-cell rp-mapping-wizard__table-cell--board">
          Board name
        </div>
        {FIELD_DEFINITIONS.map((field) => (
          <div key={field.key} className="rp-mapping-wizard__table-cell">
            {field.label}
            {field.required && (
              <span className="rp-mapping-wizard__required-star"> *</span>
            )}
          </div>
        ))}
      </div>

      <div className="rp-mapping-wizard__table-row">
        <div className="rp-mapping-wizard__table-cell rp-mapping-wizard__table-cell--board">
          {boardName}
        </div>
        {FIELD_DEFINITIONS.map((field) => {
          // For fields that accept any type (e.g. Status), show all columns
          const showAllColumns = !!field.skipTypeValidation;
          const matchingCols = showAllColumns
            ? columns
            : columns.filter(
                (col) =>
                  col.type.toLowerCase() ===
                  field.expectedType.toLowerCase().replace(' ', '_'),
              );

          return (
            <div key={field.key} className="rp-mapping-wizard__table-cell">
              <select
                className="rp-mapping-wizard__select"
                value={mapping[field.key] ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  onFieldChange(field.key, val || null);
                }}
              >
                <option value="">{/* empty */}</option>
                {matchingCols.map((col) => (
                  <option key={col.id} value={col.id}>
                    {showAllColumns ? `${col.title} (${col.type})` : col.title}
                  </option>
                ))}
                {/* Fallback: show all columns if type filter has no matches */}
                {!showAllColumns && matchingCols.length === 0 &&
                  columns.map((col) => (
                    <option key={col.id} value={col.id}>
                      {col.title} ({col.type})
                    </option>
                  ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  </>
);

// ─── Component ────────────────────────────────────────────────────────────────

const FieldMappingWizard: React.FC<FieldMappingWizardProps> = ({
  boardId,
  onComplete,
  onSkip,
}) => {
  const [step, setStep] = useState<WizardStep>('item-level');
  const [itemLevel, setItemLevel] = useState<ItemLevel>('both');

  const [mapping, setMapping] = useState<FieldMapping>({ ...EMPTY_MAPPING });
  const [subitemMapping, setSubitemMapping] = useState<FieldMapping>({ ...EMPTY_MAPPING });

  const [columns, setColumns] = useState<MondayColumn[]>([]);
  const [subitemColumns, setSubitemColumns] = useState<MondayColumn[]>([]);
  const [subitemsBoardName, setSubitemsBoardName] = useState('');

  const [boardName, setBoardName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing config and columns
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [config, boardColumns, boardInfo] = await Promise.all([
          getBoardConfig(boardId),
          getBoardColumns(boardId),
          monday.api(
            `query ($boardId: [ID!]!) { boards(ids: $boardId) { name } }`,
            { variables: { boardId: [String(boardId)] } },
          ),
        ]);
        if (!cancelled) {
          if (config.fieldMapping) {
            setMapping(config.fieldMapping);
          }
          if (config.subitemFieldMapping) {
            setSubitemMapping(config.subitemFieldMapping);
          }
          if (config.itemLevel) {
            setItemLevel(config.itemLevel);
          }
          setColumns(boardColumns);
          const boards = (boardInfo.data as { boards?: { name: string }[] })?.boards;
          setBoardName(boards?.[0]?.name ?? `Board ${boardId}`);

          // Also fetch subitems board columns
          try {
            const subResult = await getSubitemsBoardColumns(boardId);
            if (!cancelled && subResult.columns.length > 0) {
              setSubitemColumns(subResult.columns);
              setSubitemsBoardName(`Subitems of ${boards?.[0]?.name ?? `Board ${boardId}`}`);
            }
          } catch {
            // Board may not have subitems — that's fine
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load board data',
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [boardId]);

  const handleMainFieldChange = useCallback(
    (key: keyof FieldMapping, columnId: string | null) => {
      setMapping((prev) => ({ ...prev, [key]: columnId }));
    },
    [],
  );

  const handleSubitemFieldChange = useCallback(
    (key: keyof FieldMapping, columnId: string | null) => {
      setSubitemMapping((prev) => ({ ...prev, [key]: columnId }));
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError(null);

    // Validate column types before saving
    const validateMapping = (fm: FieldMapping, cols: MondayColumn[], label: string): string[] => {
      const errors: string[] = [];
      for (const field of FIELD_DEFINITIONS) {
        if (field.skipTypeValidation) continue;
        const colId = fm[field.key];
        if (!colId) continue;
        const col = cols.find((c) => c.id === colId);
        if (col && col.type.toLowerCase() !== field.expectedType.toLowerCase().replace(' ', '_')) {
          errors.push(`${label} "${field.label}" expects ${field.expectedType} but "${col.title}" is ${col.type}`);
        }
      }
      return errors;
    };

    const validationErrors: string[] = [];
    if (itemLevel === 'main' || itemLevel === 'both') {
      validationErrors.push(...validateMapping(mapping, columns, 'Main Items'));
    }
    if (itemLevel === 'sub' || itemLevel === 'both') {
      const subCols = subitemColumns.length > 0 ? subitemColumns : columns;
      validationErrors.push(...validateMapping(subitemMapping, subCols, 'Sub-items'));
    }

    if (validationErrors.length > 0) {
      setError(`Column type mismatch:\n${validationErrors.join('\n')}`);
      setIsSaving(false);
      return;
    }

    try {
      await updateBoardConfig(boardId, {
        fieldMapping: mapping,
        subitemFieldMapping: subitemMapping,
        itemLevel,
      });
      onComplete();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save field mapping',
      );
    } finally {
      setIsSaving(false);
    }
  }, [boardId, mapping, subitemMapping, columns, subitemColumns, itemLevel, onComplete]);

  const showMainMapping = itemLevel === 'main' || itemLevel === 'both';
  const showSubitemMapping = itemLevel === 'sub' || itemLevel === 'both';

  const canProceedMapping = (() => {
    if (showMainMapping && (!mapping.timelineColumnId || !mapping.assigneesColumnId)) return false;
    if (showSubitemMapping && (!subitemMapping.timelineColumnId || !subitemMapping.assigneesColumnId)) return false;
    return true;
  })();

  if (isLoading) {
    return (
      <div className="rp-mapping-wizard">
        <div className="rp-mapping-wizard__loading">
          <Loader size="medium" />
        </div>
      </div>
    );
  }

  // ── Step 1: Item Level ──────────────────────────────────────────────────────

  if (step === 'item-level') {
    return (
      <div className="rp-mapping-wizard">
        <div className="rp-mapping-wizard__card">
          <h1 className="rp-mapping-wizard__title">ITEM LEVEL</h1>

          <p className="rp-mapping-wizard__description">
            <em>
              Select the item level you want to use for scheduling.
              Choose "Main item only" to schedule main items,
              "Sub-item only" to schedule sub-items,
              or "Both" to schedule both main items and sub-items.
            </em>
          </p>

          <div className="rp-mapping-wizard__level-options">
            <label className="rp-mapping-wizard__level-option">
              <input
                type="radio"
                name="item-level"
                checked={itemLevel === 'main'}
                onChange={() => setItemLevel('main')}
              />
              <span>Main item only</span>
            </label>
            <label className="rp-mapping-wizard__level-option">
              <input
                type="radio"
                name="item-level"
                checked={itemLevel === 'sub'}
                onChange={() => setItemLevel('sub')}
              />
              <span>Sub-item only</span>
            </label>
            <label className="rp-mapping-wizard__level-option">
              <input
                type="radio"
                name="item-level"
                checked={itemLevel === 'both'}
                onChange={() => setItemLevel('both')}
              />
              <span>Both</span>
            </label>
          </div>

          <div className="rp-mapping-wizard__actions">
            <Button onClick={() => setStep('mapping')}>
              Next
            </Button>
          </div>

        </div>
      </div>
    );
  }

  // ── Step 2: Field Mapping ───────────────────────────────────────────────────

  return (
    <div className="rp-mapping-wizard">
      <div className="rp-mapping-wizard__card">
        <h1 className="rp-mapping-wizard__title">MAPPING FIELDS</h1>

        {/* Required columns notice */}
        <div className="rp-mapping-wizard__notice">
          <div className="rp-mapping-wizard__notice-icon">i</div>
          <div>
            <strong className="rp-mapping-wizard__notice-title">Required columns</strong>
            <p>
              Prior to commencing, kindly ensure that you configure
              the <strong>Timeline</strong> and <strong>Assignees</strong> settings
              in the designated section. These settings are essential for
              displaying your tasks accurately.
            </p>
            <ul className="rp-mapping-wizard__notice-fields">
              <li><strong>Timeline</strong> — date range column that defines when a task starts and ends on the scheduler.</li>
              <li><strong>Assignee</strong> — people column that determines which resource row the task appears in.</li>
              <li><strong>Original Estimate</strong> — numbers column for the total estimated hours for a task across its full timeline. This is the baseline for remaining estimate calculations: when worklogs are tracked, remaining work is calculated as Original Estimate minus logged hours. The value is spread evenly across working days (e.g. 16h over 4 days = 4h/day). You can map both columns at the board level — if a specific item has values in both, Hours Per Day takes priority.</li>
              <li><strong>Hours Per Day</strong> — numbers column for daily effort allocation. This value is applied to each working day independently (e.g. 4h/day means 4 hours on every scheduled day). Unlike Original Estimate, this rate is not reduced by worklogs — it represents a constant daily commitment. If an item has values in both columns, Hours Per Day takes priority.</li>
              <li><strong>Status</strong> — displayed as a badge on each task card. Can be a Status, Text, or Numbers column.</li>
              <li><strong>Color</strong> — color picker column that sets the task card background color.</li>
            </ul>
          </div>
        </div>

        {/* Main item mapping table */}
        {showMainMapping && (
          <MappingTable
            label="For main items"
            boardName={boardName}
            mapping={mapping}
            columns={columns}
            onFieldChange={handleMainFieldChange}
          />
        )}

        {/* Sub-item mapping table */}
        {showSubitemMapping && (
          <MappingTable
            label="For sub items"
            boardName={subitemsBoardName || `Subitems of ${boardName}`}
            mapping={subitemMapping}
            columns={subitemColumns.length > 0 ? subitemColumns : columns}
            onFieldChange={handleSubitemFieldChange}
          />
        )}

        {/* Error */}
        {error && (
          <div className="rp-mapping-wizard__error">{error}</div>
        )}

        {/* Actions */}
        <div className="rp-mapping-wizard__actions">
          <Button kind="tertiary" onClick={() => setStep('item-level')}>
            Back
          </Button>
          <Button
            onClick={handleSave}
            loading={isSaving}
            disabled={!canProceedMapping}
          >
            Next
          </Button>
        </div>

      </div>
    </div>
  );
};

export default FieldMappingWizard;
